import { Injectable, OnDestroy } from '@angular/core';
import { SocketService } from '@service/socket.service';
import { AlarmMonitoring } from 'ops/common/models/alarm-monitoring';
import { Observable, Subject, Subscription } from 'rxjs';
import { OpsAlarmApiService } from './ops-alarm-API.service';
import { NotificationService } from '@service/notification.service';
import { AlarmAnimationService } from '@service/alarm-animation.service';
import { AlarmStatus } from 'ops/common/models/alarm-level';

@Injectable({
    providedIn: 'root',
})
export class OpsAlarmMonitoringService implements OnDestroy {
    private alarmLookup: { [channel: string]: AlarmMonitoring } = {};
    private newAlarmSubscription: Subscription;
    private recoveredAlarmSubscription: Subscription;
    private alarmClassUpdatedSubscription: Subscription;
    private alarmsBeingAcked = new Set<number>();

    private buzzerIsActive: boolean = false;

    alarms: AlarmMonitoring[] = [];
    onAlarmsUpdated$ = new Subject<void>();

    constructor(
        private socketService: SocketService,
        private opsAlarmApiService: OpsAlarmApiService,
        private notificationService: NotificationService,
        private alarmAnimation: AlarmAnimationService,
    ) {
        this.newAlarmSubscription = this.socketService
            .onNewAlarm()
            .subscribe((data: AlarmMonitoring) => {
                this.buzzerIsActive = true;
                this.alarms.push(data);
                this.alarmLookup[data.channel] = data;
                this.cleanResolvedAlarms();
            });

        this.recoveredAlarmSubscription = this.socketService
            .onAlarmRecovered()
            .subscribe((data) => {
                if (this.alarmLookup[data.channel]) {
                    this.alarmLookup[data.channel].updatedAt = data.updatedAt;
                    for (let i = 0; i < this.alarms.length; i++) {
                        if (this.alarms[i].channel === data.channel) {
                            this.alarms[i].updatedAt = data.updatedAt;
                        }
                    }
                }
                this.cleanResolvedAlarms();
            });

        this.alarmClassUpdatedSubscription = this.alarmAnimation.classUpdated$.subscribe(() => {
            this.cleanResolvedAlarms();
        });
    }

    ngOnDestroy(): void {
        this.newAlarmSubscription?.unsubscribe();
        this.recoveredAlarmSubscription?.unsubscribe();
        this.alarmClassUpdatedSubscription?.unsubscribe();
    }

    getAlarms() {
        return this.alarms;
    }

    onNewAlarm() {
        const observable = new Observable((observer) => {
            const subscription = this.socketService.onNewAlarm().subscribe((v: AlarmMonitoring) => {
                observer.next(v);
            });
            return () => subscription.unsubscribe();
        });
        return observable;
    }
    clickStopBuzzerData() {
        console.log('buzzer clicked');
        this.buzzerIsActive = false;
    }

    isAnyAlarmUnacked() {
        return this.alarms.some((v) => v.UnAck);
    }

    isBuzzerActive() {
        return this.buzzerIsActive;
    }

    alarmsUpdated() {
        this.onAlarmsUpdated$.next();
    }

    async ackAllAlarms(ackedAlarms) {
        await this.opsAlarmApiService.allAlarmAcked(ackedAlarms);
        this.cleanResolvedAlarms();
    }

    async ackAlarm(alarm: AlarmMonitoring) {
        if (!alarm.UnAck) {
            this.notificationService.alarmIsAlreadyAcked(alarm.name);
            return Promise.reject(new Error(`Alarm ${alarm.name} is already ACKed`));
        } else if (this.alarmsBeingAcked.has(alarm.id)) {
            this.notificationService.alarmIsBeingAcked(alarm.name);
            return Promise.reject(new Error('Alarm is currently being acked'));
        }

        this.alarmsBeingAcked.add(alarm.id);
        try {
            await this.opsAlarmApiService.alarmAcked(alarm);
            console.log('clicked ack: ', alarm);
        } catch (err) {
            throw new Error(err);
        }
        this.alarmsBeingAcked.delete(alarm.id);
        this.cleanResolvedAlarms();
    }

    isAckInProgress(alarm: AlarmMonitoring) {
        return this.alarmsBeingAcked.has(alarm.id);
    }

    cleanResolvedAlarms() {
        let alarmsToDelete = new Set();
        this.alarms.map((data) => {
            let flag = this.statusToFlag(data.status);
            const isAcked = this.alarmAnimation.getClassByFlag(data.channel, flag);
            if (isAcked === 0 && data.updatedAt) {
                alarmsToDelete.add(data.channel);
            }
        });
        this.alarms = this.alarms.filter((data) => !alarmsToDelete.has(data.channel));
        this.alarmsUpdated();
    }

    statusToFlag(alarmStatus) {
        switch (alarmStatus) {
            case AlarmStatus.HIGHHIGH:
                return 'highHigh';
            case AlarmStatus.HIGH:
                return 'high';
            case AlarmStatus.LOW:
                return 'low';
            case AlarmStatus.LOWLOW:
                return 'lowlow';
            case AlarmStatus.FEEDBACK:
                return 'feedbackError';
        }
    }
}
